import { toast } from "@/components/ui/use-toast";
import { usePercentageStore } from "@/dashboard/store/store";
import { refreshCachedDataAfterMutation } from "@/lib/queryClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { animatePercentage } from "@/libs/utils";
interface MutationOptions<T> {
  method?: "POST" | "PUT" | "DELETE";
  id: string; // Cette valeur est utilisée pour générer la clé dynamique
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}

export function useMutateData<T>({
  method = "POST",
  id,
  onSuccess,
  onError,
}: MutationOptions<T>) {
  const { setPercentage, resetPercentage } = usePercentageStore();
  const queryClient = useQueryClient();

  const mutateData = async ({
    endpoint,
    body,
  }: {
    endpoint: string;
    body: any;
  }) => {
    let token =
      localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");
    if (!token && window.location.pathname !== "/login")
      window.location.href = "/login";

    // startLoading();
    animatePercentage(100, setPercentage);
    const response = await fetch(endpoint, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (response.status === 401) {
      token = await refreshToken();
      if (token) {
        setPercentage(100);
        return mutateData({ endpoint, body });
      } else {
        toast({ title: "Votre session a expiré, veuillez vous reconnecter." });
        localStorage.removeItem("user");
        window.location.href = "/logout";
        setPercentage(100);
        return;
      }
    }

    // endLoading();

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  };

  const refreshToken = async () => {
    const refreshToken =
      localStorage.getItem("refreshToken") ||
      sessionStorage.getItem("refreshToken");
    if (!refreshToken) throw new Error("No refresh token available");

    const response = await fetch(
      `${import.meta.env.VITE_API_ENDPOINT}/token/refresh`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${refreshToken}`,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }
    );

    if (!response.ok) throw new Error("Failed to refresh token");

    const data = await response.json();
    const newToken = data.token;
    const newRefreshToken = data.refresh_token;

    localStorage.setItem("jwtToken", newToken);
    localStorage.setItem("refreshToken", newRefreshToken);

    return newToken;
  };

  const startLoading = () => {
    const div = document.createElement("div");
    div.style.position = "fixed";
    div.style.top = "0";
    div.style.left = "0";
    div.style.width = "100%";
    div.style.height = "100%";
    div.style.zIndex = "9999";
    div.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
    div.style.display = "flex";
    div.style.justifyContent = "center";
    div.style.alignItems = "center";
    div.style.color = "white";
    div.id = "loading-cover";

    document.body.appendChild(div);
  };

  const endLoading = () => {
    const div = document.getElementById("loading-cover");
    if (div?.parentNode) {
      div.parentNode.removeChild(div);
    }
  };

  return useMutation<T, Error, { endpoint: string; body: any }>({
    mutationFn: mutateData,
    onSuccess: (data) => {
      // Invalidation de la query avec une clé dynamique
      if (id || id != "") {
        void queryClient.invalidateQueries({ queryKey: [id] }); // Utilise `id` pour invalider la bonne query
      }

      void refreshCachedDataAfterMutation();
      onSuccess?.(data);
    },
    onError: (error) => {
      toast({ title: "Une erreur est survenue.", description: error.message });
      onError?.(error);
    },
  });
}
